import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { ProductCard } from '../components/ProductCard';
import { ContactForm } from '../components/ContactForm';

export function HomePage() {
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [activeCategorySlug, setActiveCategorySlug] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProductsData() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/produkter?limit=100');
        if (res.ok) {
          const data = await res.json();
          const items = data.items || [];
          
          // Separate categories and sub-products
          const cats = items.filter(
            (item) => item.is_category_page && item.url_slug !== 'produkter' && item.url_slug !== 'produkter-kelfae-lofter-aps'
          );
          const prods = items.filter((item) => !item.is_category_page);

          setCategories(cats);
          setAllProducts(prods);

          if (cats.length > 0) {
            setActiveCategorySlug(cats[0].url_slug);
          }
        }
      } catch (err) {
        console.error('Error loading products for homepage:', err);
      } finally {
        setLoading(false);
      }
    }
    loadProductsData();
  }, []);

  return (
    <>
      <SEO
        title="Akustikloft til kontor og akustik panel i Ishøj og København | KELFAE LOFTER ApS"
        description="Hos Kelfae Lofter har vi et stort udvalg af lofter, der skaber god akustik. Din leverandør af lofter til kontor, erhverv og offentlige bygninger."
      />

      {/* Hero Block A: 2-Column Split (Light cool gray text box left | Cover Image right) - kelfae.dk layout */}
      <section className="bg-white border-b border-neutral-200">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Text Block */}
          <div className="bg-[#F4F4F4] p-8 sm:p-12 lg:p-16 flex flex-col justify-center space-y-6">
            <span className="text-xs font-bold uppercase tracking-widest text-neutral-600 font-sans">
              KELFAE LOFTER APS
            </span>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 leading-tight font-serif-heading">
              Hos Kelfae Lofter har vi et stort udvalg af lofter, der skaber <span className="underline decoration-[#7FDCA7] decoration-4">god akustik</span>
            </h1>

            <p className="text-sm sm:text-base text-neutral-700 leading-relaxed font-sans">
              Din leverandør af lofter og akustikløsninger. Vi leverer professionelle akustiklofter, T-skinnesystemer og tilbehør til arkitekter, entreprenører, tømmerhandlere og erhverv i hele Danmark.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Link
                to="/produkter"
                className="px-6 py-3 bg-[#7FDCA7] hover:bg-[#69C892] text-black font-semibold text-sm transition-all shadow-sm flex items-center gap-2"
              >
                <span>Udforsk produkter</span>
                <i className="fa-solid fa-arrow-right text-xs text-black" aria-hidden="true" />
              </Link>
              <Link
                to="/om-os"
                className="px-6 py-3 bg-neutral-200 border border-neutral-300 hover:bg-neutral-300 text-black font-semibold text-sm transition-all shadow-sm flex items-center gap-2"
              >
                <span>Læs mere om os</span>
              </Link>
            </div>
          </div>

          {/* Right Image Container */}
          <div className="relative min-h-[350px] lg:min-h-[500px] bg-neutral-900">
            <img
              src="https://assets.auxodev.dk/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/aa3-dsc7088-453.jpg"
              alt="Akustikloft og T-skinnesystem i moderne byggeri"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* Hero Block B: Inverted 2-Column Split (Cover Image left | Dark Charcoal text box right) */}
      <section className="bg-white border-b border-neutral-200">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Image Container */}
          <div className="relative min-h-[220px] sm:min-h-[260px] md:min-h-[280px] lg:min-h-[500px] max-h-[300px] lg:max-h-none bg-neutral-900 order-2 lg:order-1">
            <img
              src="https://assets.auxodev.dk/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/aa2-dsc7081-hdr-449.jpg"
              alt="Akustikplader montering"
              className="w-full h-full object-cover object-center"
            />
          </div>

          {/* Right Text Block */}
          <div className="bg-[#F4F4F4] p-8 sm:p-12 lg:p-16 flex flex-col justify-center space-y-6 text-neutral-900 order-1 lg:order-2">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 leading-tight font-serif-heading">
              Stor erfaring og et indgående kendskab til branchen
            </h2>

            <div className="space-y-4 text-xs sm:text-sm text-neutral-700 leading-relaxed font-sans">
              <p>
                Vi har mange års erfaring med at samarbejde med arkitekter, entreprenører og tømrere, og det har givet os både knowhow og et indgående kendskab til branchen og markedet inden for akustikplader.
              </p>
              <p>
                Vi har nøje sammensat et bredt sortiment af akustiklofter, således vi kan efterkomme kundernes ønsker. Vi kan blandt andet tilbyde loftplader i mineraluld og mineralfiber, og vi kan opsætte alt fra flådelofter til metalgitre. Vores speciale er T-skinnesystemer til lofter, hvor vi er blandt de førende i Danmark.
              </p>
            </div>

            <div className="pt-2">
              <Link
                to="/montageanvisning"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#7FDCA7] hover:bg-[#69C892] text-neutral-950 font-semibold text-sm transition-all"
              >
                <span>Se montageanvisning</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Navigation Banner Cards */}
      <section className="bg-black py-16 sm:py-20 lg:py-24 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              {
                title: 'Produkter',
                desc: 'Se vores samlede sortiment i loft- og akustikløsninger',
                href: '/produkter',
                img: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/cta.jpg',
              },
              {
                title: 'Galleri',
                desc: 'Se eksempler på vores udvalgte akustikprojekter',
                href: '/galleri',
                img: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/cta-parafon2.jpg',
              },
              {
                title: 'Montageanvisning',
                desc: 'Vejledning og video til korrekt opsætning',
                href: '/montageanvisning',
                img: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/g4-dsc7188-433.jpg',
              },
              {
                title: 'Om os',
                desc: 'Mangeårig knowhow og professionel rådgivning',
                href: '/om-os',
                img: 'https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/c8-dsc7157-365.jpg',
              },
            ].map((card) => (
              <Link
                key={card.title}
                to={card.href}
                className="group relative bg-neutral-900 text-white min-h-[280px] sm:min-h-[320px] p-7 sm:p-8 flex flex-col justify-between hover:border-[#7FDCA7] border border-neutral-800 transition-all shadow-md rounded-lg overflow-hidden"
              >
                {/* Background Image with Zoom on Hover */}
                <img
                  src={card.img}
                  alt={card.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-85"
                  loading="lazy"
                />

                {/* Dark Gradient Overlay for optimal legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/75 via-neutral-950/35 to-neutral-950/15 group-hover:from-neutral-950/65 group-hover:via-neutral-950/25 group-hover:to-neutral-950/10 transition-colors" />

                {/* Card Header Content */}
                <div className="relative z-10 space-y-3">
                  <h3 className="text-xl sm:text-2xl font-bold font-serif-heading text-white group-hover:text-[#7FDCA7] transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-sm text-white leading-relaxed font-sans">{card.desc}</p>
                </div>

                {/* Card Footer Link CTA */}
                <div className="relative z-10 pt-4 mt-6 border-t border-neutral-700/80 flex items-center justify-between text-sm font-semibold text-white group-hover:text-[#7FDCA7] transition-colors">
                  <span>Gå til {card.title.toLowerCase()}</span>
                  <i className="fa-solid fa-arrow-right text-sm group-hover:translate-x-1.5 transition-transform duration-300" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Products Headline & Category Hover Section */}
      <section className="py-16 sm:py-20 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-3 mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-neutral-500 font-sans">
              Sortiment
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 font-serif-heading">
              Vores produkter
            </h2>
            <p className="text-sm text-neutral-600 font-sans">
              Klik på en kategori for at se detaljer, specifikationer og produkter.
            </p>
          </div>

          {loading ? (
            <div className="py-12 text-center text-neutral-500 text-sm">
              <i className="fa-solid fa-spinner fa-spin mr-2 text-[#7FDCA7]" aria-hidden="true" />
              Henter produkter og kategorier...
            </div>
          ) : (
            <div className="space-y-12">
              {/* Category Cards Grid - 2 Columns on Tablet (sm/md), 6-Column Grid Layout on Desktop (lg) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6">
                {categories.map((cat, index) => {
                  const fallbackImage = 'https://assets.auxodev.dk/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/lofter-444.jpg';
                  const imageSrc = cat.images?.[0] || fallbackImage;
                  const isActive = activeCategorySlug === cat.url_slug;
                  const isWide =
                    cat.title === 'Flådeloft' ||
                    cat.title === 'Bafler' ||
                    cat.url_slug?.includes('fladeloft') ||
                    cat.url_slug?.includes('bafler');

                  return (
                    <div
                      key={cat.id || cat.url_slug}
                      className={`group relative col-span-1 ${isWide ? 'lg:col-span-3' : 'lg:col-span-2'}`}
                    >
                      <Link
                        to={`/produkter/${cat.url_slug}`}
                        onMouseEnter={() => setActiveCategorySlug(cat.url_slug)}
                        className={`group relative block w-full overflow-hidden bg-neutral-900 rounded-sm shadow-md transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${
                          isWide ? 'aspect-[16/9] sm:aspect-square lg:aspect-[16/9]' : 'aspect-square'
                        } border border-neutral-800/60 hover:border-[#7FDCA7] ${
                          isActive ? 'ring-2 ring-[#7FDCA7] ring-offset-2' : ''
                        }`}
                      >
                        {/* Background Image with Smooth Zoom */}
                        <img
                          src={imageSrc}
                          alt={cat.title}
                          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                          loading="lazy"
                        />

                        {/* Multi-layer Gradient Overlay for Optimal Contrast & Depth */}
                        <div className="absolute inset-0 bg-gradient-to-t from-neutral-950/90 via-neutral-950/45 to-neutral-900/25 transition-opacity duration-500 group-hover:opacity-95" />

                        {/* Top Indicator Dot & Category Index */}
                        <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10 pointer-events-none">
                          <span className="text-[10px] font-mono font-bold tracking-widest text-neutral-300/80 uppercase bg-black/40 backdrop-blur-xs px-2.5 py-1 rounded-full border border-white/10 group-hover:border-[#7FDCA7]/40 group-hover:text-white transition-all">
                            0{index + 1}
                          </span>
                          <span className="w-2 h-2 rounded-full bg-[#7FDCA7]/70 group-hover:bg-[#7FDCA7] group-hover:shadow-[0_0_8px_#7FDCA7] transition-all duration-300" />
                        </div>

                        {/* Centered Content with Serif Title & Micro-interactions */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center z-10">
                          <h3 className="font-serif-heading font-bold text-xl sm:text-2xl lg:text-3xl text-white drop-shadow-md leading-tight group-hover:text-[#7FDCA7] transition-colors duration-300">
                            {cat.title}
                          </h3>

                          {/* Decorative Accent Line */}
                          <div className="w-8 group-hover:w-16 h-0.5 bg-[#7FDCA7] my-3 rounded-full opacity-80 group-hover:opacity-100 transition-all duration-300 shadow-[0_0_6px_#7FDCA7]" />

                          {/* Hover CTA Indicator */}
                          <span className="text-xs font-semibold uppercase tracking-wider text-[#7FDCA7] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 flex items-center gap-1.5 mt-1">
                            <span>Se produkter</span>
                            <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                          </span>
                        </div>

                        {/* Bottom Subtle Glowing Border Highlight */}
                        <div className="absolute bottom-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-[#7FDCA7] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Partners Section - kelfae.dk style */}
      <section className="py-16 bg-black border-b border-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#7FDCA7] font-sans">
              Leverandører
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-serif-heading mt-1">
              Vores partnere
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 items-center justify-items-center max-w-4xl mx-auto">
            {/* Rockfon (Replaced Parafon under Samarbejdspartnere per prompt rule) */}
            <a
              href="https://www.rockfon.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/95 border border-neutral-800 p-8 w-full flex flex-col items-center justify-center space-y-3 shadow-md rounded-xs min-h-[160px] hover:border-[#7FDCA7] hover:bg-white transition-all group"
            >
              <img
                src="https://assets.auxodev.dk/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/imgi-51-default-2.png"
                alt="Rockfon Samarbejdspartner"
                className="h-20 sm:h-24 w-auto object-contain max-w-[85%] group-hover:scale-105 transition-transform"
              />
              <span className="text-sm font-semibold text-neutral-900 group-hover:text-[#7FDCA7] transition-colors font-sans flex items-center gap-1.5">
                Rockfon
                <i className="fa-solid fa-arrow-up-right-from-square text-xs text-neutral-500 group-hover:text-[#7FDCA7]" aria-hidden="true" />
              </span>
            </a>

            {/* Knauf AMF */}
            <a
              href="https://knauf.com/da-DK"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/95 border border-neutral-800 p-8 w-full flex flex-col items-center justify-center space-y-3 shadow-md rounded-xs min-h-[160px] hover:border-[#7FDCA7] hover:bg-white transition-all group"
            >
              <img
                src="https://assets.auxodev.dk/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/imgi-1-knauf-4744.png"
                alt="Knauf AMF"
                className="h-20 sm:h-24 w-auto object-contain max-w-[85%] group-hover:scale-105 transition-transform"
              />
              <span className="text-sm font-semibold text-neutral-900 group-hover:text-[#7FDCA7] transition-colors font-sans flex items-center gap-1.5">
                Knauf AMF
                <i className="fa-solid fa-arrow-up-right-from-square text-xs text-neutral-500 group-hover:text-[#7FDCA7]" aria-hidden="true" />
              </span>
            </a>

            {/* Armstrong */}
            <a
              href="https://www.armstrong.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/95 border border-neutral-800 p-8 w-full flex flex-col items-center justify-center space-y-3 shadow-md rounded-xs min-h-[160px] hover:border-[#7FDCA7] hover:bg-white transition-all group"
            >
              <img
                src="https://assets.auxodev.dk/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/imgi-1-armstrong-444.png"
                alt="Armstrong"
                className="h-20 sm:h-24 w-auto object-contain max-w-[85%] group-hover:scale-105 transition-transform"
              />
              <span className="text-sm font-semibold text-neutral-900 group-hover:text-[#7FDCA7] transition-colors font-sans flex items-center gap-1.5">
                Armstrong
                <i className="fa-solid fa-arrow-up-right-from-square text-xs text-neutral-500 group-hover:text-[#7FDCA7]" aria-hidden="true" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Callout Banner & Webform (Light Background + Clean Styling) */}
      <section className="relative w-full py-20 lg:py-28 text-neutral-900 overflow-hidden bg-neutral-50 border-t border-neutral-200">
        {/* Background Image with Light Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105 opacity-15 transform duration-700"
          style={{ backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/cta.jpg')` }}
          aria-hidden="true"
        />
        
        {/* Soft Light Backdrop Layer */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-neutral-50/90 to-white/95 backdrop-blur-xs" aria-hidden="true" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#7FDCA7]/20 via-transparent to-transparent pointer-events-none" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Information & narrative content */}
            <div className="lg:col-span-6 space-y-6">
              {/* Modern Badge */}
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-neutral-800 bg-[#7FDCA7]/25 px-3.5 py-1.5 rounded-full border border-[#7FDCA7]/40 shadow-xs">
                <i className="fa-solid fa-headset text-xs text-[#2A8C52]" aria-hidden="true" />
                <span>Faglig Rådgivning & Tilbud</span>
              </div>

              <h2 className="font-serif-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight leading-tight">
                Kontakt <span className="text-transparent bg-clip-text bg-gradient-to-r from-neutral-950 via-neutral-800 to-[#2A8C52]">KELFAE LOFTER</span> ApS
              </h2>
              
              <h3 className="font-sans text-lg lg:text-xl font-semibold text-neutral-800 leading-snug">
                Kontakt os for at høre mere om vores akustikplader og loftsystemer
              </h3>
              
              <p className="font-sans text-sm lg:text-base text-neutral-700 leading-relaxed font-normal">
                Vi benytter de førende producenter i Europa inden for akustik såsom Rockfon og Knauf AMF Thermatex Star. Disse producenter har et særligt fokus på bæredygtige materialer og produktionsmetoder. Derfor er akustikpladerne udviklet og produceret med omtanke for miljøet.
              </p>

              <p className="font-sans text-sm lg:text-base text-neutral-700 leading-relaxed font-normal">
                Du er velkommen til at høre mere om vores lydabsorberende plader, der bidrager til forbedret akustik. Du kan ringe til os på telefon{' '}
                <a href="tel:43712540" className="font-bold text-neutral-900 font-sans hover:text-[#2A8C52] transition-colors whitespace-nowrap underline decoration-[#7FDCA7] underline-offset-4">
                  (+45) 43 71 25 40
                </a>{' '}
                eller sende en mail til{' '}
                <a href="mailto:jlh@kelfae.dk" className="font-bold text-neutral-900 font-sans hover:text-[#2A8C52] transition-colors whitespace-nowrap underline decoration-[#7FDCA7] underline-offset-4">
                  jlh@kelfae.dk
                </a>. Vi laver gerne et uforpligtende tilbud på jeres næste projekt.
              </p>

              <div className="pt-2 flex flex-wrap gap-4 items-center justify-between border-t border-neutral-200 text-xs text-neutral-600 font-sans">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-location-dot text-[#2A8C52]" aria-hidden="true" />
                  <span>Vejleåvej 1, 2635 Ishøj</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-500">
                  <i className="fa-solid fa-building text-[#2A8C52]/80" aria-hidden="true" />
                  <span>CVR: 19837688</span>
                </div>
              </div>
            </div>

            {/* Right Column: Clean Light Webform Card */}
            <div className="lg:col-span-6">
              <div className="relative bg-white border border-neutral-200 rounded-2xl p-6 sm:p-8 lg:p-9 shadow-xl overflow-hidden group">
                {/* Accent Top Gradient Highlight Line */}
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#7FDCA7] to-transparent" aria-hidden="true" />
                
                <div className="mb-6 space-y-1">
                  <h3 className="font-serif-heading text-xl sm:text-2xl font-bold text-neutral-900">
                    Send os en henvendelse
                  </h3>
                  <p className="text-xs sm:text-sm text-neutral-600 font-sans">
                    Udfyld formularen, så vender vi tilbage hurtigst muligt.
                  </p>
                </div>

                <ContactForm className="w-full" isLight={true} />
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
